import { isAxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import { last } from 'ramda';
import React, { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { reportCategoryAdd } from 'modules/menu/behavioral';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { useCreateCategoriesTracking } from 'modules/menu/hooks/telemetryHooks';
import { ImageProcessingError, useProcessMenuImage } from 'modules/menu/hooks/useProcessMenuImage';
import useMenuData from 'modules/menu/useMenuData';

import { CreateCategoryForm } from './CreateCategoryForm';
import { CreateCategoryFormData } from '../../types';
import { buildCategoryCreateData } from '../../utils';

type CreateCategoryFormContainerProps = { menuEditorRootUrl: string };

const isExpectedError = (error: unknown) => error instanceof ImageProcessingError || isAxiosError(error);

export const CreateCategoryFormContainer = ({ menuEditorRootUrl }: CreateCategoryFormContainerProps) => {
  const history = useHistory();
  const intl = useIntl();
  const processMenuImage = useProcessMenuImage();
  const { menu, updateMenuData } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  const { categoryImages: isCategoryImagesEnabled, isRetail } = useContext(MenuFeaturesContext);
  const { categoryImages: hasCategoryImagesPermission } = useContext(MenuPermissionsContext);

  const handleCloseClick = useCallback(() => {
    history.push({ pathname: menuEditorRootUrl });
  }, [history, menuEditorRootUrl]);

  const trackCreateCategories = useCreateCategoriesTracking();

  const handleFormSubmit = useCallback(
    async (categories: CreateCategoryFormData) => {
      try {
        const buildCategoryCreateDataPromises = categories.map(async category => {
          const imageUrl = await processMenuImage(category.image);
          return buildCategoryCreateData(menu.primary_language, category, imageUrl);
        });
        const data = await Promise.all(buildCategoryCreateDataPromises);

        const updatedMenu = await api.bulkCreateCategory(menu.id, data);
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
        reportCategoryAdd(categories.length);
        const categoryNames = data.map(category => category.name?.[0].value ?? '');
        const hasImage = data.filter(category => !!category.image).length > 0;
        trackCreateCategories(categoryNames, hasImage);
        history.push({ pathname: `${menuEditorRootUrl}/${last(updatedMenu.categories).id}` });
        return;
      } catch (error) {
        if (!isExpectedError(error)) {
          throw error;
        }
        return { [FORM_ERROR]: intl.formatMessage({ id: 'errors.addCategoryError' }) };
      }
    },
    [history, intl, menu, menuEditorRootUrl, processMenuImage, updateMenuData, trackCreateCategories],
  );

  return (
    <CreateCategoryForm
      onCloseClick={handleCloseClick}
      onSubmit={handleFormSubmit}
      hasSubcategory={isRetail}
      hasCategoryImagesPermission={isCategoryImagesEnabled && hasCategoryImagesPermission}
    />
  );
};
